type DetailAnswer = { answered: boolean; answerIndex: number | null; correct: boolean };
type DetailStudent = { userId: string; fullName: string; answers: DetailAnswer[]; correctCount: number; totalCount: number; score20: number | null; mastery: string | null };
type DetailQuestion = { id: string; number: number; prompt: string; correctIndex: number | null };
type DetailSession = { id: string; mode: "prerequisite" | "evaluated"; questions: DetailQuestion[]; students: DetailStudent[]; questionStats: { answered: number; correct: number; percent: number }[] };

const resultLetters = ["A", "B", "C", "D"];
let decorating = false;
const detailCache = new Map<string, DetailSession>();

async function getHistorySessions() {
  const response = await fetch("/api/qcm/admin/history", { credentials: "include" });
  if (!response.ok) return [];
  return ((await response.json()).sessions ?? []).filter(Boolean);
}

function cellClass(answer: DetailAnswer) {
  if (!answer.answered) return "qcm-answer-empty";
  return answer.correct ? "qcm-answer-correct" : "qcm-answer-wrong";
}

function cellText(answer: DetailAnswer) {
  if (!answer.answered) return "—";
  if (answer.answerIndex === null) return answer.correct ? "✓" : "×";
  return resultLetters[answer.answerIndex] ?? "?";
}

async function loadDetail(sessionId: string) {
  if (detailCache.has(sessionId)) return detailCache.get(sessionId)!;
  const response = await fetch(`/api/qcm-results/sessions/${encodeURIComponent(sessionId)}`, { credentials: "include" });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error ?? "Détail du QCM impossible à charger");
  detailCache.set(sessionId, body.session);
  return body.session as DetailSession;
}

function renderDetail(container: HTMLElement, detail: DetailSession) {
  const questionsHeader = detail.questions.map((question) => `<th title="${escapeHtml(question.prompt)}">Q${question.number}</th>`).join("");
  const studentRows = detail.students.map((student) => {
    const answers = student.answers.map((answer) => `<td class="${cellClass(answer)}" title="${answer.answered ? (answer.correct ? "Réponse juste" : "Réponse fausse") : "Aucune réponse"}">${cellText(answer)}</td>`).join("");
    const score = detail.mode === "evaluated" && student.score20 !== null ? `${formatScore(student.score20)} / 20` : "—";
    return `<tr><th>${escapeHtml(student.fullName)}</th>${answers}<td class="qcm-total-cell">${student.correctCount}/${student.totalCount}</td><td class="qcm-note-cell">${score}</td></tr>`;
  }).join("");
  const stats = detail.questionStats.map((stat) => `<td class="qcm-stat-cell">${stat.percent}%</td>`).join("");

  container.innerHTML = `
    <div class="qcm-detail-toolbar">
      <div><strong>Détail par question</strong><span>${detail.students.length} élève${detail.students.length > 1 ? "s" : ""} · ${detail.questions.length} question${detail.questions.length > 1 ? "s" : ""}</span></div>
      <button type="button" class="qcm-detail-delete">Supprimer ce QCM</button>
    </div>
    <div class="qcm-detail-legend"><span><i class="correct"></i> Juste</span><span><i class="wrong"></i> Faux</span><span><i class="empty"></i> Non répondu</span></div>
    <div class="qcm-detail-table-wrap">
      <table class="qcm-detail-table">
        <thead><tr><th>Élève</th>${questionsHeader}<th>Total</th><th>Note</th></tr></thead>
        <tbody>${studentRows || `<tr><td colspan="${detail.questions.length + 3}" class="qcm-no-student">Aucun élève n’a participé.</td></tr>`}</tbody>
        ${detail.students.length ? `<tfoot><tr><th>Réussite classe</th>${stats}<td></td><td></td></tr></tfoot>` : ""}
      </table>
    </div>`;

  const deleteButton = container.querySelector<HTMLButtonElement>(".qcm-detail-delete");
  deleteButton?.addEventListener("click", async () => {
    if (!window.confirm("Supprimer définitivement ce QCM, toutes les réponses et les notes associées ?")) return;
    deleteButton.disabled = true;
    deleteButton.textContent = "Suppression…";
    const response = await fetch(`/api/qcm-results/sessions/${encodeURIComponent(detail.id)}`, { method: "DELETE", credentials: "include" });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      window.alert(body.error ?? "Suppression impossible");
      deleteButton.disabled = false;
      deleteButton.textContent = "Supprimer ce QCM";
      return;
    }
    detailCache.delete(detail.id);
    const refresh = [...document.querySelectorAll<HTMLButtonElement>(".qcm-results-page button")].find((button) => button.textContent?.includes("Actualiser"));
    if (refresh) refresh.click();
    else window.location.reload();
  });
}

function formatScore(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(".0", "");
}

function escapeHtml(value: string) {
  return String(value).replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[character] ?? character));
}

async function decorateHistory() {
  if (decorating || !window.location.pathname.startsWith("/qcm-admin")) return;
  const cards = [...document.querySelectorAll<HTMLDetailsElement>(".qcm-history-card")];
  if (!cards.length) return;
  decorating = true;
  try {
    const sessions = await getHistorySessions();
    cards.forEach((card, index) => {
      const session = sessions[index];
      if (!session || card.dataset.qcmResultsBound === session.id) return;
      card.dataset.qcmResultsBound = session.id;
      card.dataset.sessionId = session.id;

      let container = card.querySelector<HTMLElement>(".qcm-detail-enhanced");
      if (!container) {
        container = document.createElement("section");
        container.className = "qcm-detail-enhanced";
        container.innerHTML = '<div class="qcm-detail-loading">Ouvre le QCM pour voir le détail des réponses.</div>';
        card.appendChild(container);
      }

      card.addEventListener("toggle", async () => {
        if (!card.open || !container || container.dataset.loaded === "true" || container.dataset.loading === "true") return;
        container.dataset.loading = "true";
        container.innerHTML = '<div class="qcm-detail-loading">Chargement du détail…</div>';
        try {
          const detail = await loadDetail(session.id);
          renderDetail(container, detail);
          container.dataset.loaded = "true";
        } catch (reason) {
          container.innerHTML = `<div class="qcm-detail-error">${escapeHtml(reason instanceof Error ? reason.message : "Chargement impossible")}</div>`;
        } finally {
          delete container.dataset.loading;
        }
      });

      if (card.open) card.dispatchEvent(new Event("toggle"));
    });
  } catch { /* l’historique principal reste utilisable */ }
  finally { decorating = false; }
}

const qcmResultsObserver = new MutationObserver(() => { void decorateHistory(); });
qcmResultsObserver.observe(document.documentElement, { childList: true, subtree: true });
window.setTimeout(() => void decorateHistory(), 0);
